import { watch, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useCommonModalStore } from '@/stores/commonModalStore'
import { MODAL_RESULT } from '@/modules/miiaFw/types/components/commonModal'
import { getUserAttributesFromStore, signout } from '@/modules/miiaFw/MiiaFwCognito'
import MiiaFwConfig from '@/configs/config'
import { useStoreUtils } from '@/stores/util/useStoreUtils'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import { useRouterUtils } from '@/router/useRouterUtils'

/**
 * デバイスのバリデーションメッセージを表示
 * @param deviceInfo デバイス情報のリアクティブな値
 * @returns なし
 */
export const useDeviceAuthValidWatcher = (
  deviceInfo: ComputedRef<{
    deviceId?: string | undefined
    deviceName?: string | undefined
    memo?: string | undefined
    permissionStatus?: string | undefined
    addedDateTime?: number | undefined
  }>
): void => {
  const router = useRouter()
  const commonModalStore = useCommonModalStore()

  watch(deviceInfo, newValue => {
    if (!newValue.deviceId) {
      commonModalStore
        .openConfirmModal({
          title: '未登録のデバイスです',
          body: `本アプリを利用するデバイスとして登録されていません。<br>
                  デバイス登録から利用しているデバイスの登録を実施してください。`,
          okTitle: '閉じる'
        })
        .then(async result => {
          if (result === MODAL_RESULT.OK) {
            if (router.currentRoute.value.path !== ROUTER_PATHS.login) {
              router.push({ path: ROUTER_PATHS.login })
            }
          }
        })
    } else if (newValue.permissionStatus !== 'allow') {
      commonModalStore
        .openConfirmModal({
          title: 'アクセスが禁止されています',
          body: `使用されているデバイスはアクセスが禁止されています。<br>
            管理者にお問い合わせください。<br><br>
            \u3000デバイスID: ${newValue.deviceId}<br>
            デバイス名称: ${newValue.deviceName}<br>
            アクセス許可: 禁止`,
          okTitle: '閉じる'
        })
        .then(async result => {
          if (result === 'ok') {
            if (router.currentRoute.value.path !== ROUTER_PATHS.login) {
              router.push({ path: ROUTER_PATHS.login })
            }
          }
        })
    }
  })
}

/**
 * ホーム画面への遷移を制御
 * @param requiresAuthentication 認証が必要かどうかを判定するリアクティブな値
 * @returns なし
 */
export const useRouteHomeWatcher = (requiresAuthentication: ComputedRef<boolean>): void => {
  const router = useRouter()
  const { transition } = useRouterUtils(router)
  watch(requiresAuthentication, async (newValue: boolean): Promise<void> => {
    if (newValue) {
      // デバイス登録が必要であれば何もしない
      return
    }

    const governmentSettingStore = useGovernmentSettingStore()
    const storeUtils = useStoreUtils()
    const commonModalStore = useCommonModalStore()
    const attribute = await getUserAttributesFromStore()

    // 本アプリへのアクセス権が無い場合は、ダイアログ表示して強制サインアウトする
    const governmentSettingQueryParams = {
      system: MiiaFwConfig.config.sysId,
      govCode: attribute.attribute['disaster-commonattr'].govCode
    }
    await governmentSettingStore.getGovernmentSettings(governmentSettingQueryParams)
    if (!governmentSettingStore.isEmergencyAuth()) {
      commonModalStore
        .openAlertModal({
          body: 'アクセス権限がありません。',
          okTitle: '閉じる',
          okColor: 'secondary'
        })
        .then(async () => {
          await signout()
          if (router.currentRoute.value.path !== ROUTER_PATHS.login)
            router.push({ path: ROUTER_PATHS.login }) // 同じページに遷移させるとNavigation Errorになる。
          storeUtils.allReset()
        })
      return
    }
    transition(ROUTER_PATHS.home)
  })
}
